/**
 * Nerdy — Workout Tracker (web + PWA).
 *
 * ⚠️  MOBILE-FIRST. The vast majority of users are on iPhone / Android PWAs
 *     installed from Add-to-Home-Screen. When you change ANY page in this app:
 *       1. Design + test the 393×852 (iPhone) view FIRST.
 *       2. Desktop (≥768px) is a *progressive enhancement*, not the canonical view.
 *       3. Respect iOS safe-area insets (env(safe-area-inset-*)) on every fixed element.
 *       4. Keep tap targets ≥44px. Avoid hover-only affordances.
 *       5. The mobile bottom nav has 5 slots (Home/Train/Start/Compete/You).
 *          Adding a 6th breaks the layout — extend pages internally instead.
 *
 * Most pages have BOTH a `MobileX` and a desktop branch — keep the mobile one
 * as the source of truth for product decisions.
 */
import React, { Suspense, lazy, useEffect } from "react";
import "./App.css";
import "./index.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import UpdatePrompt from "./components/UpdatePrompt";
import { flushPendingEnds } from "./lib/offline-queue";
import { trackVisit } from "./lib/analytics";
import { playBigCoin, primeAudio } from "./lib/sounds";

// Eager: typical first-paint routes + the shell itself.
import Landing from "./pages/Landing";
import AuthCallback from "./pages/AuthCallback";
import AppShell from "./components/AppShell";
import Dashboard from "./pages/Dashboard";
import ExerciseLibrary from "./pages/ExerciseLibrary";
import Profile from "./pages/Profile";

// Lazy: heavy/secondary routes — loaded on demand to keep the first paint fast.
// WorkoutSession pulls in @mediapipe/tasks-vision (~34MB unminified); deferring
// it saves the biggest single dep from the initial bundle.
const WorkoutSession = lazy(() => import("./pages/WorkoutSession"));
const WorkoutSummary = lazy(() => import("./pages/WorkoutSummary"));
const History = lazy(() => import("./pages/History"));
const About = lazy(() => import("./pages/About"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Competitions = lazy(() => import("./pages/Competitions"));
const Admin = lazy(() => import("./pages/Admin"));
const Goals = lazy(() => import("./pages/Goals"));
// Public viral-loop pages (no auth required).
const ChallengePage = lazy(() => import("./pages/ChallengePage"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));

function RouteFallback() {
    // Subtle skeleton instead of a spinner — calmer perceived loading.
    return (
        <div className="skelwrap" style={{ maxWidth: 480, margin: "0 auto", padding: "40px 22px" }} data-testid="route-fallback">
            <div className="skel" style={{ width: 130, height: 12 }} />
            <div className="skel" style={{ width: 240, height: 28 }} />
            <div className="skel" style={{ height: 170, borderRadius: 16 }} />
            <div className="skel" style={{ height: 56, borderRadius: 14 }} />
        </div>
    );
}

function AppRouter() {
    const location = useLocation();
    // Retry workouts that failed to submit while offline (on load + when connection returns).
    useEffect(() => {
        const flush = () => {
            flushPendingEnds()
                .then((n) => { if (n > 0) toast.success(`Synced ${n} offline workout${n === 1 ? "" : "s"}.`); })
                .catch(() => {});
        };
        flush();
        window.addEventListener("online", flush);
        return () => window.removeEventListener("online", flush);
    }, []);
    // First-party analytics beacon — one ping per route change.
    useEffect(() => {
        trackVisit(location.pathname);
    }, [location.pathname]);
    // Push received while the app is open: play the coin ding + show it in-app.
    // First user tap unlocks the AudioContext so the sound is allowed to play.
    useEffect(() => {
        const unlock = () => { primeAudio().catch(() => {}); };
        window.addEventListener("pointerdown", unlock, { once: true });
        if (!("serviceWorker" in navigator)) return () => window.removeEventListener("pointerdown", unlock);
        const onMessage = (e) => {
            if (e.data?.type !== "nerdy-push") return;
            try { playBigCoin(); } catch { /* audio not unlocked yet */ }
            const p = e.data.payload || {};
            if (p.title) toast(p.title, { description: p.body });
        };
        navigator.serviceWorker.addEventListener("message", onMessage);
        return () => {
            navigator.serviceWorker.removeEventListener("message", onMessage);
            window.removeEventListener("pointerdown", unlock);
        };
    }, []);
    // Synchronously detect OAuth return BEFORE anything else.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Suspense fallback={<RouteFallback />}>
            <Routes>
                <Route path="/" element={<RootRoute />} />
                <Route element={<AppShell />}>
                    <Route path="/dashboard" element={<Dashboard />} />
                    <Route path="/exercises" element={<ExerciseLibrary />} />
                    <Route path="/history" element={<History />} />
                    <Route path="/leaderboard" element={<Leaderboard />} />
                    <Route path="/competitions" element={<Competitions />} />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/goals" element={<Goals />} />
                    <Route path="/admin" element={<AdminGate />} />
                </Route>
                {/* Workout pages without sidebar */}
                <Route path="/workout/:exerciseId" element={<ProtectedRoute><WorkoutSession /></ProtectedRoute>} />
                <Route path="/workout/:workoutId/summary" element={<ProtectedRoute><WorkoutSummary /></ProtectedRoute>} />
                {/* Public pages — work logged-out (viral entry points) */}
                <Route path="/c/:challengeId" element={<ChallengePage />} />
                <Route path="/u/:username" element={<PublicProfile />} />
                <Route path="/about" element={<About />} />
                <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
        </Suspense>
    );
}

function RootRoute() {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="label-tactical">Loading…</div></div>;
    if (user) return <Navigate to="/dashboard" replace />;
    return <Landing />;
}

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="label-tactical">Loading…</div></div>;
    if (!user) return <Navigate to="/" replace />;
    return children;
}

function AdminGate() {
    const { user } = useAuth();
    if (!user?.is_admin) return <Navigate to="/dashboard" replace />;
    return <Admin />;
}

function App() {
    return (
        <BrowserRouter>
            <AuthProvider>
                <AppRouter />
                <UpdatePrompt />
                <Toaster theme="dark" position="top-center" />
            </AuthProvider>
        </BrowserRouter>
    );
}

export default App;
